import { LINKS, STEPS } from '../storyScript';
import {
  BUFFY_MS, CHAT_EASE, cueTime, LINK_DRAW, LINK_GAP, OUT, spriteClaims, STEP_IDS, TEACHER_MS,
} from './buffyTrack';
import { chatPoint, homeXFor } from './layout';
import { DUR, evalKeys, keys } from './tween';

/* The scene timeline: every UI animation in the hero, as keyframes on the
   loop clock. Times are never typed by hand: they hang off Buffy's contacts
   and the track's beat marks, C('<segment>.<contact>' | 'beat:<i>:<name>'),
   so the UI re-times itself when a clip or a line changes. The builder
   records every cue it asks for (validator check 6) and the windows in which
   a story object is visible (validator check 7).

   Every step is three beats (owner, 2026-09-29), driven generically from
   storyScript STEPS (teacher line, Buffy line) and the track's beat marks:
     A  ASK     the stage is clear; the chat (the teacher's message, Buffy's
                reply, the composer) sits in the middle, larger; the
                teacher's question types in ink and is sent as a message.
     B  ANSWER  Buffy replies in text with a small gesture.
     C  SHOW    the chat eases down to the dock and back to normal size; the
                step's scene plays above it. Buffy moves with the chat (he
                never leaves its side, never jumps) and his line stays above
                the composer.
   The per-step scenes (course, bridge, cards, draft, question) hang off the
   step's `down` mark and Buffy's contacts in beat C.

   Staging rules: Buffy never covers text or a card (hero-overlap.mjs checks
   every 50 ms); a lone card sits on the axis, a pair is balanced on it; the
   stage is never blank; no props, no rotation of anything pixel.

   Coordinates: artboard px for the art layer (cards); band-layer px (stage
   px, measured from the band's top) for the chat group, the dragged file and
   the flying file icon.

   Output: { animators, used, claims, paths, pos, geo, homeAt, chatF }.
     tf     transform + opacity from keyframes
     draw   an SVG string drawing itself (stroke-dashoffset, pathLength 100)
     pulse  a short dash running along a string
     type   typed text; caret: the blinking text caret */

const typeEnd = (start, text, per) => start + Array.from(text).length * per;
// OUT (buffyTrack): the scene clears this long before a step's first beat

export function buildScene(geo, track) {
  const { L, band } = geo;
  const phone = L.kind === 'phone';
  const used = new Set();
  const C = (name, off = 0) => {
    used.add(name);
    return cueTime(track, name) + off;
  };
  const LOOP = track.loopMs;
  const S = STEP_IDS.map((id) => C(`step:${id}`));
  const B = (i, name) => C(`beat:${i}:${name}`);

  const K = new Map();
  const k = (id, ...list) => {
    if (!K.has(id)) K.set(id, []);
    K.get(id).push(...list);
  };
  const extra = [];
  const claimIds = { curriculumFile: ['dragcard', 'cchip'] };
  const enter = (at, from, ease = 'springSoft', dur = DUR.soft) => [
    [at, { o: 0, ...from }],
    [at + dur, { o: 1, s: 1, x: 0, y: 0, r: 0, sx: 1, sy: 1 }, { _: ease, o: 'out' }],
  ];
  const exit = (at) => [[at, { o: 1, y: 0 }], [at + DUR.exit, { o: 0, y: -10 }, 'in']];
  const fade = (at, dur = DUR.exit) => [[at, { o: 1, y: 0 }], [at + dur, { o: 0, y: 0 }, 'in']];
  const popIn = (at) => [[at, { o: 0, s: 0.3 }], [at + DUR.pop, { o: 1, s: 1 }, { _: 'springPop', o: 'out' }]];
  const squash = (at, sx = 1.04, sy = 0.92) => [[at, { sx: 1, sy: 1 }], [at + 80, { sx, sy }], [at + 360, { sx: 1, sy: 1 }, 'springPop']];

  /* ---------- the chat group: up (center, larger) and down (dock) ----------
     Two native copies (Scene.jsx Chat). At rest only one is visible and it
     sits at transform none (crisp text). Down: the big copy scales DOWN onto
     the dock copy's exact place, then they swap. Up: they swap, and the big
     copy grows from the dock place back to its native size. */
  const { chat } = band;
  const atDock = { y: -chat.dy, s: 1 / chat.sc }; // the big copy laid over the dock copy
  const atCenter = { y: 0, s: 1 };
  const down = (at) => {
    const end = at + CHAT_EASE;
    k('chat-big', [at, { ...atCenter }], [end, { ...atDock }, 'inOut'], [end, { o: 1 }], [end + 1, { o: 0 }, 'step']);
    k('chat-dock', [end, { o: 0 }], [end + 1, { o: 1 }, 'step']);
  };
  const up = (at) => {
    k('chat-dock', [at - 1, { o: 1 }], [at, { o: 0 }, 'step']);
    k('chat-big', [at - 1, { o: 0, ...atDock }], [at, { o: 1 }, 'step'], [at + CHAT_EASE, { ...atCenter }, 'inOut']);
  };
  k('chat-big', [0, { o: 1, ...atCenter }]);
  k('chat-dock', [0, { o: 0 }]);
  for (let i = 0; i < 5; i += 1) {
    // the chat rises as the last step's cards finish leaving (they fade from
    // S - OUT for DUR.exit, +60 for the second card): Buffy rises with it
    // and must never pass over a card that is still fading (hero-overlap).
    // It is back in the middle before the question types (S + 550).
    if (i > 0) up(S[i]);
    down(B(i + 1, 'down'));
  }
  up(LOOP - CHAT_EASE - OUT);
  const bigKs = keys(K.get('chat-big'));
  // Buffy's feet: beside the big copy's composer corner, wherever the FLIP
  // transform has it (so he stays at its side through the ease, unscaled)
  const cornerBig = chatPoint(geo, { x: band.composer.x, y: band.composer.y + band.composer.h }, 1);
  const oBig = chatPoint(geo, chat.origin, 1);
  const chatF = (t) => {
    const v = evalKeys(bigKs, t);
    return chat.dy ? 1 + v.y / chat.dy : 0;
  };
  const homeAt = (t) => {
    const v = evalKeys(bigKs, t);
    const x = oBig.x + v.s * (cornerBig.x - oBig.x);
    const y = oBig.y + v.y + v.s * (cornerBig.y - oBig.y);
    return { x: homeXFor(geo, x), y: Math.round(y) };
  };

  /* ---------- ?frame=tile: wallpaper crossfades ---------- */
  for (let i = 0; i < 5; i += 1) {
    const id = `bg-${i + 1}`;
    if (i === 0) {
      k(id, [0, { o: 1 }], [S[1] + 600, { o: 1 }], [S[1] + 601, { o: 0 }, 'step'], [LOOP - 600, { o: 0 }], [LOOP - 599, { o: 1 }, 'step']);
    } else if (i === 4) {
      k(id, [S[4], { o: 0 }], [S[4] + 600, { o: 1 }, 'inOut'], [LOOP - 600, { o: 1 }], [LOOP, { o: 0 }, 'inOut']);
    } else {
      k(id, [S[i], { o: 0 }], [S[i] + 600, { o: 1 }, 'inOut'], [S[i + 1] + 600, { o: 1 }], [S[i + 1] + 601, { o: 0 }, 'step']);
    }
  }

  /* ---------- beats A and B of every step: ask, send, answer ---------- */
  const comp = band.composer;
  const clearAt = (i) => (i < 5 ? S[i] - OUT : LOOP - CHAT_EASE - OUT - 300); // step i's stage clears
  const click = C('lookAssign.start', 100);
  for (let i = 1; i <= 5; i += 1) {
    const step = STEPS[i - 1];
    const tStart = B(i, 'type');
    const send = B(i, 'send');
    const tEnd = typeEnd(tStart, step.teacher, TEACHER_MS);
    const reply = B(i, 'reply');
    const dn = B(i, 'down');
    // the teacher's words type in ink, then lift away into the message
    extra.push({ ids: [`typed-${i}-t`], kind: 'type', text: step.teacher, start: tStart, per: TEACHER_MS, until: send + 250, scroll: true });
    // ink until it leaves (owner): it clears in one frame as the sent
    // message appears, never fading through gray
    k(`typed-${i}`, [tStart - 1, { o: 0 }], [tStart, { o: 1 }, 'step'], [send + 59, { o: 1 }], [send + 60, { o: 0 }, 'step']);
    extra.push({ ids: [`caret-${i}`], kind: 'caret', windows: [{ from: tStart - 300, to: send, typing: [tStart, tEnd] }] });
    k('send-live', [tStart, { o: 0 }], [tStart + 180, { o: 1 }], [send + 250, { o: 1 }], [send + 430, { o: 0 }]);
    k('send', [send, { s: 1 }], [send + 80, { s: 0.86 }], [send + 360, { s: 1 }, 'springPop']);
    k('ring', [tStart - 200, { o: 0.6 }], [tStart, { o: 1 }], [dn, { o: 1 }], [dn + CHAT_EASE, { o: 0.6 }]);
    // the sent message, right-aligned; it leaves as the chat goes down
    k(`bubble-${i}`, ...enter(send + 60, { y: 10, s: 0.96 }), ...fade(dn, 220));
    // Buffy's reply, typed at his pace; it stays as his line in the dock
    const out = i === 4 ? click + 250 : clearAt(i);
    k(`reply-${i}`, [reply - 1, { o: 0 }], [reply, { o: 1 }, 'step'], ...fade(out, DUR.fade));
    extra.push({ ids: [`reply-${i}-t`], kind: 'type', text: step.buffy, start: reply, per: BUFFY_MS, until: out + DUR.fade });
  }
  // step 4: his line changes once the teacher clicks Assign
  const after = click + 480;
  k('reply-4b', [after - 1, { o: 0 }], [after, { o: 1 }, 'step'], ...fade(clearAt(4), DUR.fade));
  extra.push({ ids: ['reply-4b-t'], kind: 'type', text: STEPS[3].after, start: after, per: BUFFY_MS, until: clearAt(4) + DUR.fade });
  // the placeholder shows whenever the input is empty
  const dropAt = 1250;
  const phKeys = [];
  for (let i = 1; i <= 5; i += 1) {
    const hideAt = i === 1 ? dropAt : B(i, 'type');
    const send = B(i, 'send');
    phKeys.push([hideAt - 1, { o: 1 }], [hideAt, { o: 0 }, 'step'], [send + 250, { o: 0 }], [send + 430, { o: 1 }]);
  }
  k('ph', ...phKeys);

  /* ---------- step 1: the file glides into the chat, is sent, and flies to
     Buffy's hooves; the course card reads its chapters ---------- */
  const send1 = B(1, 'send');
  const bandY = (p) => ({ x: p.x, y: p.y - band.top }); // stage px -> band layer px
  // the file's slot: the attachment row inside the taller composer, at the
  // center state (stage px)
  const slot = chatPoint(geo, { x: comp.x + comp.pad + comp.chipW / 2, y: comp.y - comp.attachH + 12 + 15 }, 1);
  // the card lands on the composer, never out over Buffy beside it (its left
  // edge hangs 24 px left of the cursor tip)
  const compLeft = chatPoint(geo, { x: comp.x, y: comp.y }, 1).x;
  const tip = { x: Math.max(slot.x + 24 - L.dragW / 2, compLeft + 8 + 24), y: slot.y + (phone ? 24 : 29) };
  const from = { x: tip.x + (phone ? 30 : 360), y: tip.y - (phone ? 120 : 150) };
  const t0 = bandY(from);
  const t1 = bandY(tip);
  k('cursorf',
    [0, { o: 1, x: t0.x, y: t0.y, s: 1 }],
    [dropAt, { x: t1.x, y: t1.y }, 'inOut'],
    [dropAt + 60, { s: 0.9 }], [dropAt + 260, { s: 1 }, 'springPop'],
    [dropAt + 200, { o: 1 }], [dropAt + 420, { o: 0, x: t1.x + 30, y: t1.y + 8 }, 'out'],
    [LOOP - 320, { o: 0, x: t0.x, y: t0.y, s: 1 }], [LOOP, { o: 1 }, 'out']);
  k('dragcard', [0, { o: 1, s: 1, r: -3 }], [dropAt - 60, { o: 1, s: 1, r: -3 }], [dropAt, { o: 0, s: 0.85, r: 0 }, 'in'],
    [LOOP - 320, { o: 0, s: 1, r: -3 }], [LOOP, { o: 1 }, 'out']);
  // the composer grows an attachment row as the file lands (the taller box,
  // drawn natively, eases in from the normal height: a short FLIP), holds
  // the file while the words type, and shrinks back once it is sent
  // only the white card scales (bottom-anchored, 'ctall-bg'); the box itself
  // just switches on and off, so the chip, the words and the send button are
  // never squashed and sit where the normal composer's do (no jump)
  const shortY = comp.h / (comp.h + comp.attachH);
  k('ctall', [dropAt - 1, { o: 0 }], [dropAt, { o: 1 }, 'step'], [send1 + 420, { o: 1 }], [send1 + 421, { o: 0 }, 'step']);
  k('ctall-bg', [dropAt, { sy: shortY }], [dropAt + 240, { sy: 1 }, 'out'], [send1 + 200, { sy: 1 }], [send1 + 420, { sy: shortY }, 'inOut']);
  k('cnorm', [dropAt - 1, { o: 1 }], [dropAt, { o: 0 }, 'step'], [send1 + 420, { o: 0 }], [send1 + 421, { o: 1 }, 'step']);
  // the chip pops in once the growing card reaches its row (about 60 ms)
  const chipAt = dropAt + 60;
  k('cchip', [chipAt, { o: 0, s: 0.7 }], [chipAt + 1, { o: 1 }, 'step'], [chipAt + 260, { s: 1 }, 'springPop'],
    [send1, { o: 1, y: 0 }], [send1 + 200, { o: 0, y: -8 }, 'in']);
  // (no flying file: the sent message keeps the file; his catch sheet draws
  // it in his hooves as he answers)

  // C: the course card reads its chapters above the docked chat; then (the
  // "Any curriculum" section's look) it slides left, the SAT skills window
  // arrives on the right, and a straight connector runs from each connected
  // chapter's row to its skill chip as Buffy plugs it in. Phone: the course
  // gives way to the connected chapters, each skill chip under its chapter.
  const course = L.course;
  const courseIn = B(1, 'down') + 300;
  const split = B(1, 'split');
  const clear1 = clearAt(1); // step 1's stage clears for step 2
  // reading the course (no icons): row by row, 110 ms apart, each chapter
  // that carries an SAT skill turns its "Ch N" blue (a 160 ms fade)
  const hold = C('present.hold');
  for (let n = 1; n <= course.show; n += 1) {
    const at = hold + 110 * (n - 1);
    k(`tick-${n}`, [at, { o: 0 }], [at + 160, { o: 1 }, 'out']);
  }
  const f1 = (n) => n.toFixed(1);
  const paths = {};
  // + 1: the card's 1 px border (.cx-surface); its rows (and the chips) are
  // laid out inside it, the connector paths in stage coordinates
  const rowY = (n) => course.y + 1 + course.rowTop + course.rowH * (n - 1) + course.rowH / 2;
  if (phone) {
    k('course', ...enter(courseIn, { y: 14 }), [split, { o: 1 }], [split + 180, { o: 0 }, 'out']);
    k('course-ring', [courseIn + 300, { o: 0 }], [courseIn + 600, { o: 1 }], [split, { o: 1 }], [split + 150, { o: 0 }]);
    k('linked', ...enter(split + 120, { y: 12 }), ...exit(clear1));
  } else {
    const slide = course.pairX - course.x;
    k('course', ...enter(courseIn, { y: 14 }),
      [split, { x: 0 }], [split + DUR.soft, { x: slide }, 'springSoft'],
      [clear1, { o: 1, x: slide }], [clear1 + DUR.exit, { o: 0, x: slide }, 'in']);
    k('course-ring', [courseIn + 300, { o: 0 }], [courseIn + 600, { o: 1 }], [split, { o: 1 }], [split + 150, { o: 0 }]);
    k('skills', ...enter(split + 150, { x: 24 }), ...exit(clear1 - 60));
  }
  // every link (owner: "the number must match what is shown"): the first
  // plugs on Buffy's connect gesture (grab, carry, extend, plug), the other
  // nine draw in a quick cascade LINK_GAP apart from that plug, so the step
  // is no longer than with three. Phone: each linked row rises in instead.
  const plug1 = C('connect1.plug');
  // all connectors and their dots leave as ONE layer (the Chromebook budget:
  // ten lines fading one by one would be twenty moving parts)
  if (!phone) k('links', [0, { o: 1 }], [clear1 - 200, { o: 1 }], [clear1, { o: 0 }, 'in']);
  LINKS.forEach((lk, idx) => {
    const i = idx + 1;
    const y = rowY(lk.chapter);
    const P = { x: course.pairX + course.w, y };
    const T = { x: (L.skills ? L.skills.x : 0) + 16, y };
    paths[`s${i}-line`] = `M${f1(P.x)} ${f1(P.y)} L${f1(T.x)} ${f1(T.y)}`;
    paths[`s${i}-dot`] = P;
    const first = idx === 0;
    const plug = first ? plug1 : plug1 + LINK_GAP * idx;
    const grab = first ? C('connect1.grab') : plug - LINK_DRAW;
    if (phone) {
      k(`lrow-${i}`, [plug - 1, { o: 0, y: 6 }], [plug + 200, { o: 1, y: 0 }, 'out']);
      return;
    }
    const draw = first
      ? [[C('connect1.carry'), { d: 0.35 }, 'inOut'], [C('connect1.extend'), { d: 0.7 }, 'inOut'], [plug, { d: 1 }, 'out']]
      : [[plug, { d: 1 }, 'out']];
    extra.push({
      ids: [`s${i}-line`],
      kind: 'draw',
      ks: keys([
        [grab, { o: 0, d: 0 }], [grab + 1, { o: 1 }, 'step'], ...draw,
      ]),
    });
    // the start dot: pops on the gesture's grab; in the cascade it simply
    // appears with its line (fewer moving parts: the Chromebook budget)
    k(`s${i}-dot`, ...(first ? popIn(grab) : [[grab - 1, { o: 0 }], [grab, { o: 1 }, 'step']]));
    // the chip lands on the plug (the gesture's plug also squashes)
    k(`chip-${i}`, ...popIn(plug), ...(first ? squash(plug + DUR.pop, 1.05, 0.92) : []));
  });

  /* ---------- step 2: where they struggle (assessment results) ---------- */
  const resIn = B(2, 'down') + 300;
  const clear2 = clearAt(2);
  k('results', ...enter(resIn, { y: 16 }), ...exit(clear2));
  k('results-ring', [resIn + 500, { o: 0 }], [resIn + 750, { o: 1 }], [clear2, { o: 1 }], [clear2 + 200, { o: 0 }]);
  // the class's bars fill one after another, then he points at the weakest
  [0, 1, 2].forEach((i) => k(`rbar-${i + 1}`, [resIn + 400 + 120 * i, { sx: 0.001 }], [resIn + 400 + 120 * i + DUR.soft, { sx: 1 }, 'springSoft']));
  const weakAt = C('pointWeak.hold');
  k('weak-fill', [weakAt, { o: 0 }], [weakAt + 240, { o: 1 }]);
  k('weak-tag', ...popIn(C('pointWeak.tap')));

  /* ---------- step 3: why (owner: the example on the left, Buffy's
     explanation on the right, both on stage together) ---------- */
  const qIn = B(3, 'down') + 300;
  const clear3 = clearAt(3);
  k('question', ...enter(qIn, { s: 0.97, y: phone ? 12 : 16 }), ...exit(clear3));
  k('question-ring', [qIn + 600, { o: 0 }], [qIn + 850, { o: 1 }], [clear3, { o: 1 }], [clear3 + 200, { o: 0 }]);
  // stacked (narrow phones) it rises in under the example; side by side it
  // slides in from the right
  k('explain', ...enter(qIn + 300, L.whyStack ? { y: 12 } : { x: 24 }), ...exit(clear3 + 60));
  const barsAt = C('lookQuestion.start', 60);
  [0, 1, 2, 3].forEach((i) => k(`bar-${i + 1}`, [barsAt + 70 * i, { sx: 0.001 }], [barsAt + 70 * i + DUR.soft, { sx: 1 }, 'springSoft']));
  k('check-c', ...popIn(barsAt + 320));
  const bHold = C('pointB.hold');
  k('bfill', [bHold, { o: 0 }], [bHold + 240, { o: 1 }]);
  k('bamber', ...enter(bHold, { sx: 0.6 }));
  k('most', ...popIn(bHold + 120));
  // the missing "- 2" lights up in the work line as he points at B
  k('why-miss', ...popIn(C('pointB.tap')));

  /* ---------- step 4: practice (draft, Assign, adaptive practice) ---------- */
  const clear4 = clearAt(4);
  const btnW = phone ? 96 : 104;
  const btnH = 32;
  // scene.css .hx-assign: right 20 / bottom 20 (phone 12 / 11), centered in
  // the draft's footer
  const btnRight = phone ? 12 : 20;
  const btnBottom = phone ? 11 : 20;
  const draftSolo = L.draft.soloX != null ? L.draft.soloX - L.draft.x : 0;
  const assignC = { x: L.draft.x + draftSolo + L.draft.w - btnRight - btnW / 2, y: L.draft.y + L.draft.h - btnBottom - btnH / 2 };
  const draftIn = B(4, 'down') + 300;
  const pairIn = click + 220;
  // alone it is on the axis (#35); it slides over as the adaptive-practice
  // card arrives, and the pair is balanced on the axis
  k('draft',
    [draftIn, { o: 0, s: 0.96, x: draftSolo, y: 16 }],
    [draftIn + DUR.soft, { o: 1, s: 1, x: draftSolo, y: 0 }, { _: 'springSoft', o: 'out' }],
    [pairIn, { x: draftSolo }], [pairIn + DUR.soft, { x: 0 }, 'springSoft'],
    ...exit(clear4));
  k('draft-ring', [draftIn + 400, { o: 0 }], [draftIn + 650, { o: 1 }], [click + 150, { o: 1 }], [click + 400, { o: 0 }]);
  k('cursor',
    [click - 900, { o: 0, x: L.cursorFrom4.x, y: L.cursorFrom4.y, s: 1 }],
    [click - 750, { o: 1 }],
    [click - 60, { x: assignC.x + 6, y: assignC.y + 4 }, 'inOut'],
    [click, { s: 1 }], [click + 80, { s: 0.86 }], [click + 300, { s: 1 }, 'springPop'],
    [click + 380, { o: 1 }], [click + 580, { o: 0 }]);
  k('assign-btn', [click, { s: 1 }], [click + 80, { s: 0.94 }], [click + 360, { s: 1 }, 'springPop']);
  k('assign-ring', [click, { o: 0, s: 1 }], [click + 100, { o: 1, s: 1.02 }], [click + 650, { o: 0, s: 1.1 }, 'out']);
  k('assign-a', [click + 80, { o: 1 }], [click + 200, { o: 0 }]);
  k('assign-b', [click + 80, { o: 0 }], [click + 200, { o: 1 }]);
  k('tag-draft', [click + 80, { o: 1 }], [click + 200, { o: 0 }]);
  k('adaptive', ...enter(pairIn, { s: 0.9, x: phone ? 0 : -24, y: phone ? -12 : 0 }), ...exit(clear4 + 60));
  k('adaptive-on', ...popIn(C('assignCheer.apex', -150)));

  /* ---------- step 5: improve over time (levels week by week) ---------- */
  const progIn = B(5, 'down') + 300;
  const clear5 = clearAt(5); // the stage clears for step 1
  k('progress', ...enter(progIn, { y: 16 }), ...exit(clear5));
  k('progress-ring', [progIn + 500, { o: 0 }], [progIn + 750, { o: 1 }], [clear5, { o: 1 }], [clear5 + 200, { o: 0 }]);
  // each row's gain grows from its start level to its end level, 120 ms
  // apart, as he looks at the card (no overshoot: it is data)
  const gainsAt = C('lookProgress.start', 100);
  [1, 2, 3].forEach((r) => k(`gain-${r}`, [gainsAt + 120 * (r - 1), { sx: 0.001 }], [gainsAt + 120 * (r - 1) + DUR.soft, { sx: 1 }, 'out']));
  const bestAt = C('progressCheer.apex', -200);
  k('best-fill', [bestAt, { o: 0 }], [bestAt + 260, { o: 1 }]);

  /* ---------- assemble ---------- */
  const animators = [];
  K.forEach((list, id) => animators.push({ ids: [id], kind: 'tf', ks: keys(list) }));
  animators.forEach((a) => {
    const m = /^bg-(\d)$/.exec(a.ids[0]);
    if (m) a.ids.push(`sh-${m[1]}`); // tile shadows follow their wallpaper
  });
  animators.push(...extra);

  // Claims (validator check 7): where the curriculum file is visible.
  const claims = [...spriteClaims(track)];
  Object.entries(claimIds).forEach(([prop, ids]) => {
    ids.forEach((id) => {
      const a = animators.find((x) => x.ids.includes(id));
      if (!a) return;
      let fromT = null;
      for (let t = 0; t <= LOOP; t += 5) {
        const on = t < LOOP && evalKeys(a.ks, t).o > 0.001;
        if (on && fromT === null) fromT = t;
        if (!on && fromT !== null) {
          claims.push({ prop, from: fromT, to: t, owner: id });
          fromT = null;
        }
      }
    });
  });

  return { animators, used, claims, paths, geo, loopMs: LOOP, steps: S, homeAt, chatF };
}
